import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, MessageSquare, Users, Phone as PhoneIcon, Paperclip, Target, ClipboardCheck, Video, Mail, FileText } from "lucide-react";
import NotesPanel from "./tabs/NotesPanel";
import ContactsPanel from "./tabs/ContactsPanel";
import ActivityPanel from "./tabs/ActivityPanel";
import CallsPanel from "./tabs/CallsPanel";
import MeetingsPanel from "./tabs/MeetingsPanel";
import MilestonesPanel from "./tabs/MilestonesPanel";
import OnboardingPanel from "./tabs/OnboardingPanel";
import EmailsPanel from "./tabs/EmailsPanel";
import ContractDetailsPanel from "./tabs/ContractDetailsPanel";
import FilesPanel from "./tabs/FilesPanel";

const TABS = [
  { key: "activity", label: "Activity", icon: Activity },
  { key: "notes", label: "Notes", icon: MessageSquare },
  { key: "contacts", label: "Contacts", icon: Users },
  { key: "calls", label: "Calls", icon: PhoneIcon },
  { key: "emails", label: "Emails", icon: Mail },
  { key: "meetings", label: "Meetings", icon: Video },
  { key: "contract", label: "Contract details", icon: FileText },
  { key: "milestones", label: "Milestones", icon: Target },
  // { key: "onboarding", label: "Onboarding", icon: ClipboardCheck },
  { key: "files", label: "Files", icon: Paperclip },
];

export default function LeadTabs({ leadId, lead }) {
  const [active, setActive] = useState("activity");
  const [focusItemId, setFocusItemId] = useState(null);

  // ActivityPanel calls this to jump to (and flash) a specific row in a sibling
  // tab. Clear focus when the user switches tabs manually so re-clicking the
  // same activity row re-triggers the highlight.
  const navigateTo = (tabKey, itemId) => {
    setFocusItemId(itemId ? String(itemId) : null);
    setActive(tabKey);
  };
  const onClickTab = (key) => {
    setFocusItemId(null);
    setActive(key);
  };
  const clearFocus = () => setFocusItemId(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div
        role="tablist"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: "0 20px",
          borderBottom: "1px solid var(--color-border-subtle)",
          overflowX: "auto",
          overflowY: "hidden", // otherwise the 1px underline overhang scrolls the row vertically
          flexShrink: 0,
          background: "var(--color-bg-surface)",
        }}
      >
        {TABS.map((t) => {
          const isActive = t.key === active;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => onClickTab(t.key)}
              style={{
                position: "relative",
                background: "transparent",
                border: "none",
                height: 34,
                padding: "0 9px",
                cursor: "pointer",
                color: isActive ? "var(--color-text-primary)" : "var(--color-text-secondary)",
                fontSize: 13,
                fontWeight: isActive ? 500 : 400,
                display: "flex",
                alignItems: "center",
                gap: 7,
                whiteSpace: "nowrap",
                transition: "color 120ms ease",
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = "var(--color-text-primary)"; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = "var(--color-text-secondary)"; }}
            >
              <Icon size={14} />
              {t.label}
              {t.soon && (
                <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "var(--color-bg-muted)", color: "var(--color-text-secondary)", fontWeight: 600 }}>
                  Soon
                </span>
              )}
              {isActive && (
                <motion.div
                  layoutId="crm-tab-underline"
                  transition={{ type: "tween", ease: [0.32, 0.72, 0, 1], duration: 0.24 }}
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 2,
                    background: "var(--color-text-secondary)",
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, overflowY: "auto", minHeight: 0, background: "var(--color-bg-base)" }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            style={{ padding: "20px 24px 24px" }}
          >
            {active === "activity" && <ActivityPanel leadId={leadId} lead={lead} onNavigate={navigateTo} />}
            {active === "notes" && <NotesPanel leadId={leadId} focusItemId={focusItemId} onFocusResolved={clearFocus} />}
            {active === "contacts" && <ContactsPanel leadId={leadId} focusItemId={focusItemId} onFocusResolved={clearFocus} />}
            {active === "calls" && <CallsPanel leadId={leadId} lead={lead} focusItemId={focusItemId} onFocusResolved={clearFocus} />}
            {active === "emails" && <EmailsPanel leadId={leadId} lead={lead} focusItemId={focusItemId} onFocusResolved={clearFocus} />}
            {active === "meetings" && <MeetingsPanel leadId={leadId} lead={lead} focusItemId={focusItemId} onFocusResolved={clearFocus} />}
            {active === "contract" && <ContractDetailsPanel leadId={leadId} lead={lead} />}
            {active === "milestones" && <MilestonesPanel leadId={leadId} focusItemId={focusItemId} onFocusResolved={clearFocus} />}
            {active === "onboarding" && <OnboardingPanel leadId={leadId} lead={lead} />}
            {active === "files" && <FilesPanel leadId={leadId} focusItemId={focusItemId} onFocusResolved={clearFocus} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
